"use client";

import { useEffect, useState } from "react";
import { useCart } from "@/contexts/CartContext";
import { useAnalytics } from "@/lib/analytics/client";
import { AnalyticsEvents, trackClientEvent } from "@/lib/analytics/events";
import { getStock } from "@/lib/commerce/stock";
import { toast } from "sonner";
import { FALLBACK_IMAGE, formatPrice } from "@/lib/shared/utils";
import type { AddToCartProduct } from "@/types/product";

interface StickyMobileATCProps {
  product: AddToCartProduct;
  sizes: string[];
  archiveMode?: boolean;
}

export default function StickyMobileATC({ product, sizes, archiveMode }: StickyMobileATCProps) {
  const { addItem } = useCart();
  const analytics = useAnalytics();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      const cta = document.getElementById("pdp-cta");
      if (!cta) {
        setVisible(false);
        return;
      }
      const rect = cta.getBoundingClientRect();
      setVisible(rect.bottom < 0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const hasAnyStock = sizes.some((size) => getStock(product.stock, size) > 0);
  const isOutOfStock = !product.is_preorder && !hasAnyStock;
  const isLocked = product.product_visibility === "preview" || archiveMode;

  const handleAdd = () => {
    if (isOutOfStock) {
      toast.error(`${product.name} is out of stock`);
      return;
    }
    if (sizes.length > 1) {
      document.getElementById("pdp-cta")?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    const size = sizes[0] ?? "";
    if (!product.is_preorder && getStock(product.stock, size) <= 0) {
      toast.error(`Size ${size} is out of stock`);
      return;
    }
    addItem({
      id: product.id,
      name: product.name,
      price: product.price,
      image: product.images?.[0] || FALLBACK_IMAGE,
      size,
      quantity: 1,
      stock: product.stock,
      is_preorder: product.is_preorder,
      purchase_limit: product.purchase_limit ?? null,
      preorder_limit: product.preorder_limit ?? null,
    });
    trackClientEvent(analytics, AnalyticsEvents.AddToCart, {
      value: product.price,
      currency: "eur",
      is_preorder: product.is_preorder,
      items: [
        {
          item_id: String(product.id),
          item_name: product.name,
          item_variant: size || undefined,
          price: product.price,
          quantity: 1,
        },
      ],
    });
    toast.success(`${product.name} (${size}) added to cart`);
  };

  if (isLocked) return null;

  const label = isOutOfStock
    ? "Sold out"
    : product.is_preorder
      ? "Pre-order"
      : "Add to cart";

  return (
    <div
      role="region"
      aria-label="Add to cart"
      className={`bg-background border-foreground/10 fixed inset-x-0 bottom-0 z-30 border-t-2 px-3 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] transition-transform duration-300 lg:hidden ${
        visible ? "translate-y-0" : "translate-y-full"
      }`}
    >
      <div className="flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-foreground truncate text-xs font-bold tracking-widest uppercase">
            {product.name}
          </p>
          <p className="text-muted-foreground text-[10px] tracking-widest uppercase">
            {formatPrice(product.price)}
          </p>
        </div>
        <button
          type="button"
          onClick={handleAdd}
          disabled={isOutOfStock}
          className="bg-foreground text-background hover:bg-foreground/90 disabled:bg-muted disabled:text-muted-foreground rounded-none px-6 py-3 text-xs font-black tracking-[0.2em] uppercase transition-colors disabled:cursor-not-allowed"
        >
          {label}
        </button>
      </div>
    </div>
  );
}
